import { FC, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import { FormikHelpers, useFormikContext } from 'formik';
import { StepModel } from '../../../../models/form/step';
import { Actions, ComparisonOperators, Condition, Rule, Target } from '../../../../models/form/rule';
import { getInitialValue } from './FormikBuilder';
import { FieldModel } from '../../../../models/form/field';
import { SectionModel } from '../../../../models/form/section';
import { BuildedValues } from './FormModels';
import { buildFieldMultipleId, getFieldMultipleId } from './FormUtilities';
import { RulesTemplates } from '../../../../pages/registrations/admin/components/rules/RulesTemplates';
import moment from 'moment';

export const addSection = async (
  values: BuildedValues,
  sectionId: string,
  steps: StepModel[],
  rules: Rule[],
  setSteps: (steps: StepModel[]) => void,
  setFieldValue: FormikHelpers<FieldModel['defaultValue']>['setFieldValue'],
): Promise<void> => {
  let stepsClone = _.cloneDeep(steps);
  for (const step of stepsClone) {
    for (const section of step.sections) {
      if (section.id === sectionId) {
        const sectionClone = _.cloneDeep(section);
        delete sectionClone.sectionsFormik;

        //Change all ids
        sectionClone.id = uuidv4();
        for (const field of sectionClone.fields) {
          let newId = '';
          if (section.sectionsFormik) {
            newId = buildFieldMultipleId(field.id, (section.sectionsFormik?.length + 1).toString());
          } else {
            newId = buildFieldMultipleId(field.id, '1');
          }
          field.id = newId;
        }

        //Add initialValues in formik
        for (const field of sectionClone.fields) {
          setFieldValue(field.id, getInitialValue(field));
        }

        if (section.sectionsFormik) {
          section.sectionsFormik.push(sectionClone);
        } else {
          section.sectionsFormik = [sectionClone];
        }
      }
    }
  }
  RulesHandlerLogic(values, stepsClone, rules, (theSteps) => {
    stepsClone = theSteps;
  });
  await setSteps(stepsClone);
};

export const removeSection = async (
  values: BuildedValues,
  sectionId: string,
  steps: StepModel[],
  rules: Rule[],
  setSteps: (steps: StepModel[]) => void,
  setFieldValue: FormikHelpers<FieldModel['defaultValue']>['setFieldValue'],
): Promise<void> => {
  let stepsClone = _.cloneDeep(steps);
  for (const step of stepsClone) {
    for (const section of step.sections) {
      if (section.sectionsFormik) {
        const removedSections = _.remove(section.sectionsFormik, {
          id: sectionId,
        });
        for (const removedSection of removedSections) {
          for (const field of removedSection.fields) {
            if (values[field.id] !== undefined) {
              setFieldValue(field.id, undefined);
            }
          }
        }
      }
    }
  }
  RulesHandlerLogic(values, stepsClone, rules, (theSteps) => {
    stepsClone = theSteps;
  });
  await setSteps(stepsClone);
};

const checkField = (field: FieldModel, value: any, condition: Condition): boolean => {
  if (value) {
    const fieldValue = RulesTemplates[field.type].formatValueType(value);
    const conditionValue = RulesTemplates[field.type].formatValueType(condition.value);
    const isValueMoment = moment.isMoment(fieldValue);
    switch (condition.comparisonOperator) {
      case ComparisonOperators.IsEqual:
        return isValueMoment ? fieldValue.isSame(conditionValue) : fieldValue == conditionValue;
      case ComparisonOperators.GreaterThan:
        return isValueMoment ? fieldValue.isAfter(conditionValue) : fieldValue > conditionValue;
      case ComparisonOperators.EqualOrGreaterThan:
        return isValueMoment ? fieldValue.isSameOrAfter(conditionValue) : fieldValue > conditionValue;
      case ComparisonOperators.LowerThan:
        return isValueMoment ? fieldValue.isBefore(conditionValue) : fieldValue > conditionValue;
      case ComparisonOperators.EqualOrLowerThan:
        return isValueMoment ? fieldValue.isSameOrBefore(conditionValue) : fieldValue > conditionValue;
      case ComparisonOperators.IsChecked:
        return value instanceof Array ? value.length > 0 : value == true;
      case ComparisonOperators.Includes:
        return value.includes(conditionValue);
      case ComparisonOperators.IsCompleted:
        return !!value;
    }
  }
  return false;
};

const applyRule = (target: Target, field: FieldModel, isTrue: boolean): boolean => {
  switch (target.action) {
    case Actions.IsVisible:
      if (isTrue) {
        if (!field.visible) {
          field.visible = true;
          return true;
        } else {
          return false;
        }
      } else {
        if (field.visible) {
          field.visible = false;
          return true;
        } else {
          return false;
        }
      }
    case Actions.IsRequired:
      if (isTrue) {
        if (!field.required) {
          field.required = true;
          return true;
        } else {
          return false;
        }
      } else {
        if (field.required) {
          field.required = false;
          return true;
        } else {
          return false;
        }
      }
  }
};

const checkCondition = (steps: StepModel[], values: Record<string, unknown>, condition: Condition): boolean => {
  for (const step of steps) {
    for (const section of step.sections) {
      if (section.multiple && section.sectionsFormik) {
        for (const aSection of section.sectionsFormik) {
          for (const field of aSection.fields) {
            const fieldId = field.id.slice(0, field.id.indexOf('@'));
            if (fieldId === condition.fieldId && field.visible) {
              return checkField(field, values[field.id], condition);
            }
          }
        }
      } else {
        for (const field of section.fields) {
          if (field.id === condition.fieldId && field.visible) {
            return checkField(field, values[field.id], condition);
          }
        }
      }
    }
  }
  return false;
};

const applyChangesForTarget = (
  steps: StepModel[],
  target: Target,
  setSteps: (steps: StepModel[]) => void,
  isTrue: boolean,
): boolean => {
  const stepsTemp = [...steps];
  for (const step of stepsTemp) {
    for (const section of step.sections) {
      if (section.multiple && section.sectionsFormik) {
        for (const aSection of section.sectionsFormik) {
          for (const field of aSection.fields) {
            const fieldId = field.id.slice(0, field.id.indexOf('@'));
            if (fieldId === target.fieldId) {
              if (applyRule(target, field, isTrue)) {
                setSteps(stepsTemp);
                return true;
              }
            }
          }
        }
      } else {
        for (const field of section.fields) {
          if (field.id === target.fieldId) {
            if (applyRule(target, field, isTrue)) {
              setSteps(stepsTemp);
              return true;
            }
          }
        }
      }
    }
  }
  return false;
};

const createRulesForMultipleSections = (
  section: SectionModel,
  rule: Rule,
  values: BuildedValues,
  steps: StepModel[],
  setSteps: (steps: StepModel[]) => void,
) => {
  if (section.sectionsFormik && section.sectionsFormik.length > 0) {
    for (const sectionsFormik of section.sectionsFormik) {
      const newConditions: Condition[][] = [];
      for (const orConditions of rule.conditions) {
        const newConditions2: Condition[] = [];
        for (const andCondition of orConditions) {
          let foundField = false;
          for (const field of sectionsFormik.fields) {
            if (getFieldMultipleId(field) === andCondition.fieldId) {
              foundField = true;
              newConditions2.push({
                ...andCondition,
                fieldId: field.id,
              });
            }
          }
          if (!foundField) {
            newConditions2.push(andCondition);
          }
        }
        newConditions.push(newConditions2);
      }
      const newTargets: Target[] = [];
      for (const target of rule.targets) {
        for (const field of sectionsFormik.fields) {
          if (getFieldMultipleId(field) === target.fieldId) {
            newTargets.push({
              ...target,
              fieldId: field.id,
            });
          }
        }
      }
      const newRule: Rule = {
        id: uuidv4(),
        name: '',
        conditions: newConditions,
        targets: newTargets,
      };
      RulesHandlerLogic(values, steps, [newRule], setSteps);
    }
  }
};

//The rules handler main function. Verifies and applies rules.
export const RulesHandlerLogic = (
  values: BuildedValues,
  steps: StepModel[],
  rules: Rule[],
  setSteps: (steps: StepModel[]) => void,
): void => {
  let changedSomething = false;
  for (const rule of rules) {
    if (rule.sectionMultipleId) {
      for (const step of steps) {
        for (const section of step.sections) {
          if (section.id === rule.sectionMultipleId) {
            createRulesForMultipleSections(section, rule, values, steps, setSteps);
            break;
          }
        }
      }
    }
    let isRuleTrue = false;
    for (const orConditions of rule.conditions) {
      let isOrTrue = true;
      for (const andCondition of orConditions) {
        if (!checkCondition(steps, values, andCondition)) {
          isOrTrue = false;
          break;
        }
      }
      if (isOrTrue) {
        isRuleTrue = true;
        break;
      }
    }
    for (const target of rule.targets) {
      if (applyChangesForTarget(steps, target, setSteps, isRuleTrue)) {
        changedSomething = true;
      }
    }
  }
  //Re-verify for nested rules.
  if (changedSomething) {
    RulesHandlerLogic(values, steps, rules, setSteps);
  }
};

interface Props {
  steps: StepModel[];
  rules: Rule[];
  setSteps: (steps: StepModel[]) => void;
}

const RulesHandler: FC<Props> = ({ steps, setSteps, rules }: Props) => {
  const { values } = useFormikContext<BuildedValues>();
  useEffect(() => {
    RulesHandlerLogic(values, steps, rules, setSteps);
  }, [values]);

  return null;
};

export default RulesHandler;
